/**
 * Secure Messenger for iframe
 *
 * Handles secure postMessage communication with HMAC authentication,
 * replay protection, and origin validation
 */

import { AccountAbstractionError } from '../../lib/errors'
import type { SecureMessage } from './types'

interface PendingMessage {
  resolve: (value: any) => void
  reject: (error: Error) => void
  timeout: number
}

export class SecureMessenger {
  private allowedOrigins: Set<string> = new Set()
  private usedNonces: Set<string> = new Set()
  private pendingMessages: Map<string, PendingMessage> = new Map()
  private channelToken: string | null = null
  private messageHandlers: Array<(message: SecureMessage, origin: string) => Promise<void>> = []

  private readonly MESSAGE_TIMEOUT = 60000 // 60 seconds
  private readonly NONCE_CLEANUP_INTERVAL = 5 * 60 * 1000 // 5 minutes

  // Debug flag: set to true to log all filtered messages (for debugging only)
  // @ts-ignore - injected during build
  private readonly DEBUG_FILTERED_MESSAGES = __EMBARK_DEBUG_FILTERED_MESSAGES__ === 'true'

  constructor() {
    // Periodic nonce cleanup
    setInterval(() => this.cleanupOldNonces(), this.NONCE_CLEANUP_INTERVAL)
  }

  /**
   * Setup message listener with security validation
   */
  setupListener(handler: (message: SecureMessage, origin: string) => Promise<void>): void {
    this.messageHandlers.push(handler)

    window.addEventListener('message', async (event) => {
      try {
        await this.handleIncomingMessage(event)
      } catch (error) {
        console.error('[iframe][Messenger] Error handling message:', error)
      }
    })

    console.log('[iframe][Messenger] Message listener setup')
  }

  /**
   * Handle incoming postMessage
   */
  private async handleIncomingMessage(event: MessageEvent): Promise<void> {
    const message = event.data
    const { origin } = event

    // Quick filter: ignore messages that are clearly not ours
    // This prevents spam from browser extensions, dev tools, etc.
    if (!message || typeof message !== 'object' || !message.type) {
      // Silently ignore messages without basic structure
      if (this.DEBUG_FILTERED_MESSAGES && message) {
        console.debug('[iframe][Messenger] Filtered: no type field', message)
      }
      return
    }

    // Check if this looks like our message format
    // Our messages always have: type, messageId, timestamp, nonce
    if (!this.validateMessageType(message)) {
      // Silently ignore messages that don't match our protocol
      if (this.DEBUG_FILTERED_MESSAGES) {
        console.debug('[iframe][Messenger] Filtered: not EmbarkAI message', {
          type: message.type,
          origin
        })
      }
      return
    }

    // Full structure validation for our messages
    if (!this.validateMessageStructure(message)) {
      console.error('[iframe][Messenger] Invalid EmbarkAI message structure:', {
        type: message.type,
        hasMessageId: !!message.messageId,
        hasTimestamp: !!message.timestamp,
        hasNonce: !!message.nonce,
        hasProjectId: !!message.projectId,
        hasData: 'data' in message
      })
      return
    }

    // For SDK_AUTH, accept HTTPS origins (initial connection)
    // In development, allow localhost/127.0.0.1 over HTTP
    if (message.type === 'SDK_AUTH') {
      const isLocalhost = origin.startsWith('http://localhost:') || origin.startsWith('http://127.0.0.1:')
      const isHttps = origin.startsWith('https://')

      if (!isHttps && !isLocalhost) {
        console.error('[iframe][Messenger] Only HTTPS origins allowed (or localhost in dev)')
        return
      }
    } else {
      // For other messages, validate origin is in allowed list
      if (!this.isAllowedOrigin(origin)) {
        console.error(`[iframe][Messenger] Rejected message from unauthorized origin: ${origin}`)
        return
      }
    }

    // Validate timestamp (must be recent)
    const messageAge = Date.now() - message.timestamp
    if (messageAge < 0 || messageAge > this.MESSAGE_TIMEOUT) {
      console.error(`[iframe][Messenger] Message timestamp invalid (age: ${messageAge}ms)`)
      return
    }

    // Check nonce uniqueness (prevent replay attacks)
    if (this.usedNonces.has(message.nonce)) {
      console.error('[iframe][Messenger] Nonce already used (replay attack?)')
      return
    }

    // Validate HMAC if SDK channel is established
    if (this.channelToken && message.hmac) {
      const expectedHmac = await this.computeHMAC(message)
      if (!this.constantTimeCompare(expectedHmac, message.hmac)) {
        console.error('[iframe][Messenger] HMAC validation failed')
        return
      }
    }

    // Mark nonce as used
    this.usedNonces.add(message.nonce)

    // Handle message
    for (const handler of this.messageHandlers) {
      await handler(message, origin)
    }
  }

  /**
   * Send response to parent
   */
  sendResponse(messageId: string, data: any, targetOrigin: string): void {
    if (!window.parent) {
      console.error('[iframe][Messenger] No parent window')
      return
    }

    const response = {
      type: 'IFRAME_MSG_RESPONSE',
      messageId,
      timestamp: Date.now(),
      data
    }

    window.parent.postMessage(response, targetOrigin)
    console.log(`[iframe][Messenger] Sent response to ${targetOrigin}`)
  }

  /**
   * Send error to parent
   */
  sendError(messageId: string, error: string | Error, targetOrigin: string): void {
    if (!window.parent) {
      console.error('[iframe][Messenger] No parent window')
      return
    }

    const errorMessage = error instanceof Error ? error.message : error
    const errorCode = error instanceof AccountAbstractionError ? error.code : undefined
    const errorDetails = error instanceof AccountAbstractionError ? error.details : undefined

    const response = {
      type: 'IFRAME_MSG_ERROR',
      messageId,
      timestamp: Date.now(),
      error: errorMessage,
      code: errorCode,
      details: errorDetails
    }

    window.parent.postMessage(response, targetOrigin)
    console.log(
      `[iframe][Messenger] Sent error to ${targetOrigin}: ${errorMessage}${errorCode ? ` [${errorCode}]` : ''}`
    )
  }

  /**
   * Add allowed origin
   */
  addAllowedOrigin(origin: string): void {
    this.allowedOrigins.add(origin)
    console.log(`[iframe][Messenger] Added allowed origin: ${origin}`)
  }

  /**
   * Remove allowed origin
   */
  removeAllowedOrigin(origin: string): void {
    this.allowedOrigins.delete(origin)
    console.log(`[iframe][Messenger] Removed allowed origin: ${origin}`)
  }

  /**
   * Set channel token for HMAC authentication
   * @deprecated Use setChannelToken instead
   */
  setSessionToken(token: string): void {
    this.setChannelToken(token)
  }

  /**
   * Set SDK channel token for HMAC authentication
   */
  setChannelToken(token: string): void {
    this.channelToken = token
    console.log('[iframe][Messenger] SDK channel token set')
  }

  /**
   * Quick check if message looks like EmbarkAI protocol
   * This filters out browser extension messages, dev tools, etc.
   */
  private validateMessageType(message: any): boolean {
    // Our messages must have these specific types
    const validTypes = [
      'SDK_AUTH',
      'SDK_CHANNEL_HEARTBEAT',
      'AUTHENTICATE',
      'START_DKG',
      'SIGN_TRANSACTION',
      'SIGN_TYPED_DATA',
      'GET_ADDRESS',
      'CHECK_KEYSHARE',
      'GET_TRUSTED_APPS',
      'REMOVE_TRUSTED_APP',
      'CLEAR_AUTHORIZATIONS',
      'CREATE_BACKUP',
      'ENCRYPT_BACKUP_DATA',
      'ENCRYPT_ENVELOPE_DATA',
      'DECRYPT_AND_RESTORE_ENVELOPE',
      'RESTORE_BACKUP',
      'RESTORE_FROM_FILE',
      'GET_BACKUP_STATUS',
      'ENSURE_SERVER_BACKUP',
      'GET_CLOUD_PROVIDERS',
      'SIGN_IN_START',
      'SIGN_IN_FINISH',
      'SIGN_OUT',
      'GET_SESSION',
      'GET_DAPP_TOKENS',
      'ACCOUNT_REQUEST',
      'REQUEST_NEW_TOKEN',
      'RESPONSE',
      'ERROR',
      'TRUSTED_APP_REMOVED',
      'TOKEN_REFRESHED'
    ]

    const validTypeGroups = ['BINANCE_', 'RAMPNOW_', 'FINCHPAY_', 'HYPERLANE_', 'USER_POLICY_', 'USER_ACTIVITY_LOG_'] // Any message starting with these prefixes is valid

    const isTypeValid = validTypes.includes(message.type)
    const isGroupTypeValid = validTypeGroups.some((prefix) => message.type.startsWith(prefix))

    if (!message.type || (!isTypeValid && !isGroupTypeValid)) return false

    // Our messages always have messageId and timestamp
    // This filters out most non-valid messages
    return !!(message.messageId && message.timestamp)
  }

  /**
   * Validate message structure
   */
  private validateMessageStructure(message: any): boolean {
    return (
      message &&
      typeof message === 'object' &&
      typeof message.type === 'string' &&
      typeof message.messageId === 'string' &&
      typeof message.timestamp === 'number' &&
      typeof message.nonce === 'string' &&
      typeof message.projectId === 'string' &&
      'data' in message
    )
  }

  /**
   * Check if origin is allowed
   */
  private isAllowedOrigin(origin: string): boolean {
    // Check if in allowed list
    if (this.allowedOrigins.has(origin)) {
      return true
    }

    // During initialization, accept HTTPS or localhost
    if (this.allowedOrigins.size === 0) {
      const isLocalhost = origin.startsWith('http://localhost:') || origin.startsWith('http://127.0.0.1:')
      const isHttps = origin.startsWith('https://')
      return isHttps || isLocalhost
    }

    return false
  }

  /**
   * Compute HMAC-SHA256 for message authentication
   */
  private async computeHMAC(message: SecureMessage): Promise<string> {
    if (!this.channelToken) {
      throw new Error('No SDK channel token for HMAC')
    }

    const encoder = new TextEncoder()
    const payload = JSON.stringify({
      type: message.type,
      messageId: message.messageId,
      timestamp: message.timestamp,
      nonce: message.nonce,
      data: JSON.stringify(message.data)
    })

    const data = encoder.encode(payload)
    const key = encoder.encode(this.channelToken)

    const cryptoKey = await crypto.subtle.importKey('raw', key, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])

    const signature = await crypto.subtle.sign('HMAC', cryptoKey, data)

    return Array.from(new Uint8Array(signature))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  }

  /**
   * Constant-time string comparison (prevent timing attacks)
   */
  private constantTimeCompare(a: string, b: string): boolean {
    if (a.length !== b.length) return false

    let result = 0
    for (let i = 0; i < a.length; i++) {
      result |= a.charCodeAt(i) ^ b.charCodeAt(i)
    }

    return result === 0
  }

  /**
   * Cleanup old nonces to prevent memory growth
   */
  private cleanupOldNonces(): void {
    if (this.usedNonces.size > 1000) {
      // Keep only the most recent 500 nonces
      const noncesArray = Array.from(this.usedNonces)
      this.usedNonces = new Set(noncesArray.slice(-500))
      console.log('[iframe][Messenger] Cleaned up old nonces')
    }
  }

  /**
   * Get messenger statistics
   */
  getStats(): {
    allowedOrigins: number
    usedNonces: number
    pendingMessages: number
  } {
    return {
      allowedOrigins: this.allowedOrigins.size,
      usedNonces: this.usedNonces.size,
      pendingMessages: this.pendingMessages.size
    }
  }
}
